import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";

/**
 * Admin → Mobile user → "Trial follow-up emails" (3 Nov 2026 meeting canvas, board 4).
 * The three emails sent when a free trial ends without a plan: when each goes, its status,
 * a preview, a test send to the signed-in admin, and stop / restart for this family.
 */
const STATUS = {
  sent: { label: "Sent", bg: "#E8F5E9", color: "#1B5E20" },
  billing: { label: "Billing email", bg: "rgba(0,0,0,0.08)", color: "rgba(0,0,0,0.7)" },
  due: { label: "Due", bg: "#FFF4E5", color: "#8A4B00" },
  scheduled: { label: "Scheduled", bg: "rgba(0,0,0,0.08)", color: "rgba(0,0,0,0.7)" },
  skipped: { label: "Skipped (window passed)", bg: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.6)" },
  missed: { label: "Window passed", bg: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.6)" },
  blocked: { label: "Not sent", bg: "#FDECEA", color: "#B71C1C" },
};

export default function TrialFollowUpPanel({ userId, canUpdate }) {
  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`admin/mobile-users/${userId}/trial-followup`);
      setData(res.data?.data || null);
    } catch {
      setData(null);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || data.ready === false) return null;

  const trialEnded = Boolean(data.trial_end);
  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };

  const openPreview = () =>
    run("preview", async () => {
      const res = await api.post(`admin/mobile-users/${userId}/trial-followup/preview`, { step });
      setPreview(res.data?.data || null);
    });
  const sendTest = () =>
    run("test", async () => {
      const res = await api.post(`admin/mobile-users/${userId}/trial-followup/send-test`, { step });
      toast.success(res.data?.message || "Test email sent.");
    });
  const toggleStop = () =>
    run("stop", async () => {
      const res = await api.post(`admin/mobile-users/${userId}/trial-followup/stop`, { stop: !data.stopped });
      toast.success(res.data?.message);
      await load();
    });

  return (
    <Paper sx={{ p: 2, mt: 3, boxShadow: "0 0 0 2px #7C3AED, 0 1px 3px rgba(0,0,0,0.12)" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Trial follow-up emails</Typography>
          {data.stopped ? <Chip size="small" color="error" label="Stopped" /> : null}
        </Stack>
        {trialEnded ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Email" value={step} onChange={(e) => setStep(Number(e.target.value))} sx={{ minWidth: 200 }}>
              <MenuItem value={1}>1 · Weak areas</MenuItem>
              <MenuItem value={2}>2 · Progress in the trial</MenuItem>
              <MenuItem value={3}>3 · Last reminder</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={openPreview} disabled={busy !== ""}>
              {busy === "preview" ? <CircularProgress size={18} /> : "Preview"}
            </Button>
            {canUpdate ? (
              <>
                <Button variant="outlined" onClick={sendTest} disabled={busy !== ""}>
                  {busy === "test" ? <CircularProgress size={18} /> : "Send test to me"}
                </Button>
                <Button variant="outlined" color={data.stopped ? "primary" : "error"} onClick={toggleStop} disabled={busy !== ""}>
                  {data.stopped ? "Restart emails" : "Stop emails"}
                </Button>
              </>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="body2" color="text.secondary">
        Sent when a free trial ends and the family doesn't choose a plan: the child's weak areas on day 1, their
        trial progress on day 3, a last reminder on day 10. Stops as soon as they pay or unsubscribe.
      </Typography>
      {data.blocked ? (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: "#B71C1C" }}>
          Not sending: {data.blocked}.
        </Typography>
      ) : null}
      {data.rows?.length ? (
        <Box sx={{ overflowX: "auto", mt: 1.5 }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead sx={{ bgcolor: "#f9fafb" }}>
              <TableRow>
                {["Email", "When", "Status"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#374151" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r) => {
                const s = STATUS[r.status] || STATUS.scheduled;
                return (
                  <TableRow key={r.step}>
                    <TableCell sx={{ fontWeight: r.step === "billing" ? 400 : 600 }}>
                      {r.step === "billing" ? r.label : `${r.step} · ${r.label}`}
                    </TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.color, fontWeight: 500 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>{preview?.subject}</DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: "#EDE9FE" }}>
          {preview ? (
            <iframe title="Email preview" srcDoc={preview.html} sandbox="" style={{ width: "100%", height: "70vh", border: 0, display: "block" }} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
